import { z } from "zod";
import { CONDITIONS } from "@/lib/condition";

export const BACKUP_FORMAT = "nebula-records-backup";
// v2 added `books` (2026-07). v3 added item media/sleeve condition
// (2026-08). Older archives still import fine — every added field is
// optional and defaults to empty/ungraded.
export const BACKUP_VERSION = 3;

/** Folder inside the archive that holds the actual image files. */
export const BACKUP_IMAGES_DIR = "images";
/** Name of the metadata file inside the archive. */
export const BACKUP_MANIFEST = "library.json";

/** Image references inside the archive are the bare `<uuid>.<ext>` basename of
    an `/uploads/...` file. Validate before ever using one to read a zip entry
    so a tampered archive can't point at `../../etc/passwd`. */
export const IMAGE_BASENAME_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(jpg|jpeg|png|webp)$/i;

const imageName = z.string().regex(IMAGE_BASENAME_REGEX);

const conditionSchema = z.enum(CONDITIONS);

const labelSchema = z.object({ name: z.string(), catno: z.string().nullable().optional() });
const creditSchema = z.object({ name: z.string(), role: z.string().default("") });
const companySchema = z.object({ name: z.string(), entityType: z.string().default("") });

const trackSchema = z.object({
  position: z.number().int(),
  title: z.string(),
  duration: z.string().nullable().optional(),
  lyrics: z.string().nullable().optional(),
});

const locationSchema = z.object({
  id: z.string(),
  name: z.string(),
  parentId: z.string().nullable(),
  hidden: z.boolean().default(false),
});

const itemSchema = z.object({
  type: z.enum(["CD", "CASSETTE", "VINYL"]),
  title: z.string(),
  artist: z.string(),
  year: z.number().int().nullable().optional(),
  originalYear: z.number().int().nullable().optional(),
  released: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  genres: z.array(z.string()).default([]),
  styles: z.array(z.string()).default([]),
  barcode: z.string().nullable().optional(),
  // Decimal is serialized as a string to avoid float rounding.
  price: z.union([z.number(), z.string()]).nullable().optional(),
  currency: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  catalogNotes: z.string().nullable().optional(),
  mediaCondition: conditionSchema.nullable().optional(),
  sleeveCondition: conditionSchema.nullable().optional(),
  discogsReleaseId: z.number().int().nullable().optional(),
  discogsArtistId: z.number().int().nullable().optional(),
  discogsMasterId: z.number().int().nullable().optional(),
  labels: z.array(labelSchema).nullable().optional(),
  credits: z.array(creditSchema).nullable().optional(),
  companies: z.array(companySchema).nullable().optional(),
  locationId: z.string().nullable().optional(),
  coverImage: imageName.nullable().optional(),
  photos: z.array(imageName).default([]),
  tracks: z.array(trackSchema).default([]),
  createdAt: z.string().optional(),
});

const bookSchema = z.object({
  title: z.string(),
  subtitle: z.string().nullable().optional(),
  author: z.string(),
  openLibraryAuthorId: z.string().nullable().optional(),
  format: z.enum(["HARDCOVER", "PAPERBACK", "OTHER"]).default("PAPERBACK"),
  isbn: z.string().nullable().optional(),
  publisher: z.string().nullable().optional(),
  publishedYear: z.number().int().nullable().optional(),
  originalTitle: z.string().nullable().optional(),
  originalYear: z.number().int().nullable().optional(),
  language: z.string().nullable().optional(),
  translator: z.string().nullable().optional(),
  edition: z.string().nullable().optional(),
  series: z.string().nullable().optional(),
  seriesVolume: z.string().nullable().optional(),
  pageCount: z.number().int().nullable().optional(),
  subjects: z.array(z.string()).default([]),
  description: z.string().nullable().optional(),
  // Decimal is serialized as a string to avoid float rounding.
  price: z.union([z.number(), z.string()]).nullable().optional(),
  currency: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  openLibraryEditionId: z.string().nullable().optional(),
  googleBooksId: z.string().nullable().optional(),
  metadataSources: z.array(z.string()).default([]),
  locationId: z.string().nullable().optional(),
  coverImage: imageName.nullable().optional(),
  photos: z.array(imageName).default([]),
  createdAt: z.string().optional(),
});

export const backupSchema = z.object({
  format: z.literal(BACKUP_FORMAT),
  version: z.number().int(),
  exportedAt: z.string().optional(),
  library: z
    .object({
      visibility: z.enum(["PUBLIC", "PRIVATE"]).optional(),
      showPricePublic: z.boolean().optional(),
      showLocationPublic: z.boolean().optional(),
    })
    .optional(),
  defaultLocationId: z.string().nullable().optional(),
  locations: z.array(locationSchema).default([]),
  items: z.array(itemSchema).default([]),
  books: z.array(bookSchema).default([]),
});

export type Backup = z.infer<typeof backupSchema>;
export type BackupItem = z.infer<typeof itemSchema>;
export type BackupBook = z.infer<typeof bookSchema>;
export type BackupLocation = z.infer<typeof locationSchema>;

/** `/uploads/<uuid>.<ext>` -> `<uuid>.<ext>`, or null if it isn't one of ours. */
export function basenameFromUploadPath(uploadPath: string | null | undefined): string | null {
  if (!uploadPath) return null;
  const base = uploadPath.replace(/^.*\//, "");
  return IMAGE_BASENAME_REGEX.test(base) ? base : null;
}
